/**
 * API client for the reading interface.
 * Fully local-first architecture — all books stored in IndexedDB.
 *
 * This is the facade class. Heavy implementations are in sub-modules:
 *   bookManager.js    — book upload/download/cloud operations
 *   languageDetect.js  — Unicode + franc-min language detection
 *   statsApi.js        — reading statistics & analytics API
 */

import * as localDB from '../localDB';
import { performSync, isSyncEnabled, mergeProgressWithLocal } from '../syncService';
import { getUserIdFromJWT, generateId } from '../utils';
import { reportError } from '../error-reporter';
import { API_BASE, apiHeaders } from './helpers';

// Import sub-module methods for mixin
import * as bookManager from './bookManager';
import * as languageDetect from './languageDetect';
import * as statsApi from './statsApi';
import * as chunkApi from './chunkApi';
import * as curatedApi from './curatedApi';

class ApiClient {
    constructor() {
        this.token = null;
        this.userId = null;
    }

    setToken(token) {
        this.token = token;
        this.userId = token ? getUserIdFromJWT(token) : null;
    }

    // Trigger background sync
    async triggerSync() {
        if (this.token) {
            // Don't await - let it run in background
            performSync(this.token).catch((err) => {
                console.error(err);
                reportError(err?.message || 'triggerSync failed', { level: 'warn', stack: err?.stack, metadata: { source: 'api.triggerSync' } });
            });
        }
    }

    // Generate UUID
    generateId() {
        return generateId();
    }

    // Get file extension
    getFileType(fileName) {
        return fileName.split('.').pop().toLowerCase();
    }

    /**
     * Check if a metadata value is usable (not a generic placeholder).
     * Returns the trimmed value if good, or null if it's a generic placeholder.
     */
    static isUsableMetadata(value) {
        if (!value || typeof value !== 'string') return null;
        const trimmed = value.trim();
        if (!trimmed) return null;
        const bad = /^(unknown|untitled|no\s*title|n\/a|none|author|\.epub|\.pdf)$/i;
        return bad.test(trimmed) ? null : trimmed;
    }

    static parseFilenameMetadata(fileName) {
        const base = fileName.replace(/\.[^/.]+$/, '');
        const sourceStripped = base.replace(/\s*\([^)]*(?:lib|source|z-lib|calibre|pdfdrive|gutenberg)[^)]*\)\s*$/i, '');

        const parenMatch = sourceStripped.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
        if (parenMatch) {
            return { title: parenMatch[1].trim(), author: parenMatch[2].trim() };
        }

        const dashParts = sourceStripped.split(/\s+-\s+/);
        if (dashParts.length === 2 && dashParts[0].length > 2 && dashParts[1].length > 2) {
            return { title: dashParts[0].trim(), author: dashParts[1].trim() };
        }

        return { title: sourceStripped.trim() || base.trim(), author: null };
    }

    // Keep detectLanguageFromText as a static method (delegates to standalone function)
    static detectLanguageFromText(text) {
        return languageDetect.detectLanguageFromText(text);
    }

    // =====================================================
    // Books - Core CRUD (simple localDB wrappers)
    // =====================================================

    async getBooks() {
        const books = await localDB.getBooks(this.userId);
        this.triggerSync();
        return { books };
    }

    async getLocalFileBookIds() {
        return localDB.getLocalFileBookIds();
    }

    async getBook(id) {
        const book = await localDB.getBook(id);
        if (!book) {
            const err = new Error('Book not found');
            err.code = 'BOOK_NOT_FOUND';
            throw err;
        }
        return { book };
    }

    async updateBook(id, data) {
        const book = await localDB.updateBook(id, data);
        this.triggerSync();
        return { book };
    }

    /** Directly PATCH a book on the remote server (bypasses local-first sync). */
    async patchBookRemote(id, data) {
        if (!this.token) return;
        const resp = await fetch(`${API_BASE}/reader/books/${id}`, {
            method: 'PATCH',
            headers: apiHeaders(this.token),
            body: JSON.stringify(data),
        });
        if (!resp.ok) throw new Error('patchBookRemote failed');
        return resp.json();
    }

    /**
     * Fetch a single book record directly from the server, bypassing the
     * local-first IndexedDB cache. Used by Library polling for in-progress
     * PDF conversions where chapters_completed / chapters_total need to be
     * fresh on every tick (sync's fire-and-forget behavior is too slow
     * for a 5-second progress display).
     *
     * Returns the book object on 200, null otherwise (caller handles).
     */
    async getBookRemote(id) {
        if (!this.token) return null;
        try {
            const resp = await fetch(`${API_BASE}/reader/books/${id}`, {
                headers: apiHeaders(this.token),
            });
            if (!resp.ok) return null;
            const data = await resp.json();
            return data?.book || null;
        } catch { return null; }
    }

    /**
     * Phase B in-app broadcast notifications. Returns { notifications: [...] }
     * with one entry per active row matching the user's plan/visibility
     * filters. Anonymous (no token) returns empty — banner stays hidden.
     * Best-effort: any network/parse error degrades to an empty list so
     * a transient backend hiccup doesn't surface as a red error to readers.
     */
    async getActiveNotifications() {
        if (!this.token) return { notifications: [] };
        try {
            const resp = await fetch(`${API_BASE}/v1/notifications`, {
                headers: apiHeaders(this.token),
            });
            if (!resp.ok) return { notifications: [] };
            const data = await resp.json();
            return { notifications: Array.isArray(data?.notifications) ? data.notifications : [] };
        } catch { return { notifications: [] }; }
    }

    // =====================================================
    // Highlights - Fully local first
    // =====================================================

    async getHighlights(bookId) {
        const highlights = await localDB.getHighlights(bookId, this.userId);
        this.triggerSync();
        return { highlights };
    }

    async createHighlight(data) {
        const highlight = await localDB.createHighlight(this.userId, data);
        this.triggerSync();
        return { highlight };
    }

    async updateHighlight(id, data) {
        const highlight = await localDB.updateHighlight(id, data);
        this.triggerSync();
        return { highlight };
    }

    async deleteHighlight(id) {
        await localDB.deleteHighlight(id);
        this.triggerSync();
        return { success: true };
    }

    // =====================================================
    // Notes - Fully local first
    // =====================================================

    async getNotes(bookId) {
        const notes = await localDB.getNotes(bookId, this.userId);
        this.triggerSync();
        return { notes };
    }

    async createNote(data) {
        const note = await localDB.createNote(this.userId, data);
        this.triggerSync();
        return { note };
    }

    async updateNote(id, data) {
        const note = await localDB.updateNote(id, data);
        this.triggerSync();
        return { note };
    }

    async deleteNote(id) {
        await localDB.deleteNote(id);
        this.triggerSync();
        return { success: true };
    }

    // =====================================================
    // Bookmarks - Fully local first
    // =====================================================

    async getBookmarks(bookId) {
        const bookmarks = await localDB.getBookmarks(bookId, this.userId);
        this.triggerSync();
        return { bookmarks };
    }

    async createBookmark(data) {
        const bookmark = await localDB.createBookmark(this.userId, data);
        this.triggerSync();
        return { bookmark };
    }

    async updateBookmark(id, data) {
        const bookmark = await localDB.updateBookmark(id, data);
        this.triggerSync();
        return { bookmark };
    }

    async deleteBookmark(id) {
        await localDB.deleteBookmark(id);
        this.triggerSync();
        return { success: true };
    }

    // =====================================================
    // Progress - Fully local first
    // =====================================================

    /**
     * Fetch this book's progress directly from the server (authoritative,
     * Cache-Control: no-store). Bounded by a 2.5s AbortController timeout so a
     * hung server can never stall a book open. Returns the server row (carrying
     * the server-clock updated_at) or null on any failure / offline / no token.
     * NOTE: the GET response omits user_id — callers must inject it before
     * keying into the local map for a merge.
     */
    async getProgressRemote(bookId) {
        if (!this.token) return null;
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 2500);
        try {
            const resp = await fetch(
                `${API_BASE}/reader/progress?book_id=${encodeURIComponent(bookId)}`,
                { headers: apiHeaders(this.token), signal: ctrl.signal }
            );
            if (!resp.ok) return null;       // 401/5xx → caller falls back to local
            const data = await resp.json();
            return data?.progress || null;
        } catch {
            return null;                      // offline / abort / parse → local fallback
        } finally {
            clearTimeout(timer);
        }
    }

    /**
     * Local-first read of a book's progress, reconciled with the server on open
     * (pull-on-open). A fresh open / reload now reflects another device's latest
     * position instead of whatever the last 30s background sync happened to land
     * in IDB. Best-effort and non-blocking:
     *   - free users (sync disabled), offline, or no token → pure local read.
     *   - any server error / timeout → silently fall back to the local row.
     * Reuses the SAME mergeProgressWithLocal as background sync, so an unsynced
     * local edit (_pending) or a newer server-clock high-water-mark still wins
     * exactly as mid-session. The local row is re-read AFTER the network call so
     * a position the user scrolled to during the round-trip is never clobbered,
     * and the merged row is persisted only when it is NOT _pending.
     */
    async getProgress(bookId) {
        const local = await localDB.getProgress(bookId, this.userId);
        if (!isSyncEnabled() || !this.token) {
            return { progress: local };
        }
        const remote = await this.getProgressRemote(bookId);
        if (!remote) return { progress: local };
        // Re-read AFTER the round-trip: the user may have scrolled (writing a
        // fresh _pending row) while we awaited the network. Never clobber it.
        const latestLocal = await localDB.getProgress(bookId, this.userId);
        if (latestLocal && latestLocal._pending) {
            return { progress: latestLocal };
        }
        const remoteRow = { ...remote, user_id: this.userId, book_id: bookId };
        const map = latestLocal ? new Map([[`${this.userId}:${bookId}`, latestLocal]]) : new Map();
        const merged = mergeProgressWithLocal([remoteRow], map)[0];
        if (merged && !merged._pending) {
            try {
                // bulkPut (not updateProgress): write WITHOUT marking _pending or
                // bumping the client updated_at — identical to background sync's
                // merged-progress write (syncService.js).
                await localDB.bulkPut('progress', [merged]);
            } catch { /* IDB write best-effort; merged still drives this render */ }
        }
        return { progress: merged || latestLocal || local };
    }

    async updateProgress(bookId, data) {
        const progress = await localDB.updateProgress(this.userId, bookId, data);
        // When the page is hidden (being unloaded / frozen), the full performSync
        // chain may not complete — iOS kills non-keepalive fetches on pagehide.
        // Send a lightweight keepalive fetch directly with just the progress data
        // to guarantee it reaches the server, bypassing the syncInProgress guard.
        if (document.visibilityState === 'hidden' && this.token) {
            this._emergencyProgressPush(bookId, data);
        }
        this.triggerSync();
        return { progress };
    }

    /**
     * Best-effort keepalive fetch for progress during page unload.
     * Bypasses syncService entirely — no IndexedDB reads, no batching, just
     * a tiny POST that the browser keeps alive after the page is gone.
     */
    _emergencyProgressPush(bookId, data) {
        // Plan-scope gate: this path bypasses performSync's own syncEnabled
        // guard, so it must check independently. syncEnabled is maintained by
        // AuthContext (READER-side paid only) and force-disabled on a server
        // 403 — without this check, a free or Lens-only user would fire an
        // unauthorized POST /reader/sync on every tab-hide.
        if (!isSyncEnabled()) return;
        try {
            fetch(`${API_BASE}/reader/sync`, {
                method: 'POST',
                headers: apiHeaders(this.token),
                body: JSON.stringify({
                    // Tell the server we don't need any data back — avoids a
                    // wasteful full-sync query whose response we'd discard anyway.
                    last_sync: new Date().toISOString(),
                    changes: {
                        progress: [{
                            book_id: bookId,
                            cfi: data.cfi || null,
                            percentage: data.percentage ?? 0,
                            chapter: data.chapter || null,
                            page: data.page ?? null,
                            finished_at: data.finished_at ?? null,
                        }],
                        books: [],
                        highlights: [],
                        notes: [],
                        bookmarks: [],
                    },
                }),
                keepalive: true,
            });
            // Fire-and-forget — no await, no .then()
        } catch { /* best effort */ }
    }

    async getAllProgress() {
        const progress = await localDB.getAllProgress(this.userId);
        return { progress };
    }

    /**
     * Stage A: read PDF page quota for the current user.
     * Returned shape (see backend services/entitlements.ts:PdfQuotaSummary):
     *   { monthlyUsed, monthlyLimit, dailyUsed, dailyLimit, perBookCap,
     *     currentMonth, currentDay, adminBypass }
     * Returns null if the user is not signed in (no token to send).
     */
    async getPdfQuota() {
        if (!this.token) return null;
        const tzMin = -new Date().getTimezoneOffset(); // minutes east of UTC, matches X-Timezone-Offset contract
        const resp = await fetch(`${API_BASE}/reader/quota/pdf`, {
            headers: { ...apiHeaders(this.token, null), 'X-Timezone-Offset': String(tzMin) },
        });
        if (!resp.ok) {
            const e = new Error(`getPdfQuota failed (${resp.status})`);
            e.status = resp.status;
            throw e;
        }
        return resp.json();
    }

}

// Mix in sub-module methods onto ApiClient.prototype
Object.assign(ApiClient.prototype, bookManager);
Object.assign(ApiClient.prototype, languageDetect);
Object.assign(ApiClient.prototype, statsApi);
Object.assign(ApiClient.prototype, chunkApi);
Object.assign(ApiClient.prototype, curatedApi);

export const api = new ApiClient();
export default api;
